import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Search, CheckCircle2, Clock, Trophy, ChevronRight } from 'lucide-react';
import { useSounds } from '@/hooks/useSounds';

// Kelime Avı Oyunu - Cümle içinde hedef kelimeyi bul
export default function WordHunt({ title, sentences, targetWords, onAnswer }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedWord, setSelectedWord] = useState(null);
  const [foundWords, setFoundWords] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const { playCorrect, playIncorrect, playClick, playLessonComplete } = useSounds();

  const currentSentence = sentences[currentIndex];
  const currentTarget = targetWords[currentIndex];
  const isLastSentence = currentIndex === sentences.length - 1;

  // Timer
  useEffect(() => {
    if (gameStarted && !gameEnded && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setGameEnded(true);
      playLessonComplete();
    }
  }, [gameStarted, gameEnded, timeLeft, playLessonComplete]);

  const words = currentSentence.split(/\s+/).map((word, idx) => {
    // Noktalama işaretlerini ayır
    const cleanWord = word.replace(/[.,!?;:]/g, '');
    const punctuation = word.match(/[.,!?;:]/g)?.join('') || '';
    return { word: cleanWord, punctuation, index: idx };
  });

  const handleWordClick = (word) => {
    if (showResult || gameEnded) return;
    
    playClick();
    setSelectedWord(word);
    setShowResult(true);
    
    const isCorrect = word.toLowerCase() === currentTarget.word.toLowerCase();
    if (isCorrect) {
      playCorrect();
      setScore(prev => prev + 10);
      setFoundWords([...foundWords, currentTarget.word]);
    } else {
      playIncorrect();
    }
  };

  const handleNext = () => {
    if (isLastSentence) {
      setGameEnded(true);
      playLessonComplete();
    } else {
      setCurrentIndex(prev => prev + 1);
      setSelectedWord(null);
      setShowResult(false);
    }
  };

  const handleComplete = () => {
    const passed = score >= sentences.length * 5;
    if (onAnswer) onAnswer(passed, passed ? 1 : 0);
  };

  // Start Screen
  if (!gameStarted) {
    return (
      <div className="w-full max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
          <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Search className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
          <p className="text-gray-600 mb-6">🎯 Kelime Avı Oyunu</p>
          
          <div className="bg-amber-50 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-bold text-amber-800 mb-2">Nasıl Oynanır?</h3>
            <ul className="text-sm text-amber-700 space-y-2">
              <li>• Her cümlede gizli bir İngilizce kelime var</li>
              <li>• Türkçe ipucuna bakarak doğru kelimeyi bul</li>
              <li>• Kelimeye tıkla ve puan kazan!</li>
              <li>• 60 saniye süren var, hızlı ol! ⏱️</li>
            </ul>
          </div>
          
          <p className="text-sm text-gray-500 mb-4">
            {sentences.length} cümle · Her doğru +10 puan
          </p>
        </div>
        
        <Button
          onClick={() => setGameStarted(true)}
          className="w-full py-4 text-lg font-bold bg-amber-500 hover:bg-amber-600 rounded-xl"
        >
          🎮 Oyunu Başlat
        </Button>
      </div>
    );
  }

  // End Screen
  if (gameEnded) {
    return (
      <div className="w-full max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
          <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Trophy className="w-10 h-10 text-amber-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Oyun Bitti! 🎉</h3>
          <div className="text-5xl font-bold text-amber-500 mb-2">{score}</div>
          <p className="text-gray-600 mb-4">puan</p>
          
          <div className="bg-gray-100 rounded-xl p-4 mb-4">
            <p className="text-sm text-gray-600">
              {foundWords.length} / {sentences.length} kelime bulundu
            </p>
          </div>
          
          {score >= sentences.length * 5 ? (
            <p className="text-green-600 font-medium">✅ Harika! Kelime avcısı oldun!</p>
          ) : (
            <p className="text-orange-600 font-medium">💪 Biraz daha pratik yapmalısın!</p>
          )}
        </div>
        
        <Button
          onClick={handleComplete}
          className="w-full py-4 text-lg font-bold bg-emerald-500 hover:bg-emerald-600 rounded-xl"
        >
          Devam Et
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header with Timer */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-100 rounded-xl">
            <Search className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <p className="text-sm text-gray-500">Kelime Avı</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-amber-600 font-bold">
            <Trophy className="w-5 h-5" />
            {score}
          </div>
          <div className={`flex items-center gap-1 font-bold ${timeLeft <= 10 ? 'text-red-500' : 'text-gray-600'}`}>
            <Clock className="w-5 h-5" />
            {timeLeft}s
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div 
          className="bg-amber-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / sentences.length) * 100}%` }}
        />
      </div>

      {/* Hint Box */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6">
        <p className="text-sm text-amber-800">
          🎯 <strong>Bul:</strong> "{currentTarget.translation}" anlamına gelen kelimeyi bul!
        </p>
      </div>

      {/* Sentence Card */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 mb-6">
        <p className="text-sm text-gray-500 mb-4">Cümle {currentIndex + 1}/{sentences.length}</p>
        
        <div className="flex flex-wrap gap-2">
          {words.map((item, idx) => {
            let wordStyle = 'bg-gray-100 hover:bg-gray-200 text-gray-800';
            
            if (showResult) {
              if (item.word.toLowerCase() === currentTarget.word.toLowerCase()) {
                wordStyle = 'bg-green-500 text-white';
              } else if (item.word === selectedWord) {
                wordStyle = 'bg-red-500 text-white';
              }
            }

            return (
              <button
                key={idx}
                onClick={() => handleWordClick(item.word)}
                disabled={showResult}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${wordStyle}`}
              >
                {item.word}{item.punctuation}
              </button>
            );
          })}
        </div>

        {/* Result */}
        {showResult && (
          <div className={`mt-6 p-4 rounded-xl ${
            selectedWord?.toLowerCase() === currentTarget.word.toLowerCase()
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {selectedWord?.toLowerCase() === currentTarget.word.toLowerCase() ? (
                <><CheckCircle2 className="w-5 h-5 text-green-500" /><span className="font-bold text-green-700">Doğru! +10 puan</span></>
              ) : (
                <><span className="font-bold text-red-700">Yanlış! Doğru cevap: {currentTarget.word}</span></>
              )}
            </div>
            {currentTarget.example && (
              <p className="text-sm text-gray-600 mt-2">
                📝 {currentTarget.example}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Next Button */}
      {showResult && (
        <Button
          onClick={handleNext}
          className="w-full py-4 text-lg font-bold bg-amber-500 hover:bg-amber-600 rounded-xl"
        >
          {isLastSentence ? 'Sonuçları Gör' : 'Sonraki Cümle'}
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      )}
    </div>
  );
}
